import React, { useEffect, useState, useRef } from 'react';
import { Layers, FileText } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
function AnimatedNumber({
  value,
  suffix = ''
}: {
  value: number;
  suffix?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 1500;
      const increment = value / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref} data-id="element-211">
      {count}
      {suffix}
    </span>;
}
export function MegapackIntro() {
  return <section className="py-8 md:py-14 bg-brand-900 text-white relative overflow-hidden" data-id="element-212">
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-213"></div>

      <div className="max-w-5xl mx-auto px-4 relative z-10 text-center" data-id="element-214">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="inline-block border-2 border-brand-400 rounded-xl md:rounded-2xl px-3 py-2 md:px-8 md:py-4 mb-4 md:mb-8 bg-brand-800/50 backdrop-blur-sm" data-id="element-215">
          <h2 className="text-xl md:text-5xl font-black mb-0.5 md:mb-2" data-id="element-216">
            PROFESORADO RESUELTO™
          </h2>
          <p className="text-xs md:text-2xl font-semibold text-brand-200" data-id="element-217">
            MEGAPACK — SISTEMA COMPLETO
          </p>
        </motion.div>

        <motion.h3 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-base md:text-3xl font-bold mb-5 md:mb-10 max-w-2xl mx-auto leading-snug" data-id="element-218">
          6 Tomos + 6 Bonos — Todos los años de la carrera — Todo resuelto.
        </motion.h3>

        <div className="flex flex-row justify-center gap-2.5 md:gap-6 mb-6 md:mb-12" data-id="element-219">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex items-center justify-center gap-2 md:gap-3 bg-brand-800 rounded-xl p-2.5 md:p-4 border border-brand-700 flex-1 max-w-[180px] md:max-w-[200px]" data-id="element-220">
            <Layers className="w-5 h-5 md:w-8 md:h-8 text-brand-300 flex-shrink-0" data-id="element-221" />
            <div className="text-left" data-id="element-222">
              <p className="font-black text-base md:text-2xl leading-none" data-id="element-223">
                <AnimatedNumber value={400} suffix="+" data-id="element-224" />
              </p>
              <p className="text-[9px] md:text-sm text-brand-200 font-medium leading-tight mt-0.5" data-id="element-225">
                páginas
              </p>
            </div>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex items-center justify-center gap-2 md:gap-3 bg-brand-800 rounded-xl p-2.5 md:p-4 border border-brand-700 flex-1 max-w-[180px] md:max-w-[200px]" data-id="element-226">
            <FileText className="w-5 h-5 md:w-8 md:h-8 text-brand-300 flex-shrink-0" data-id="element-227" />
            <div className="text-left" data-id="element-228">
              <p className="font-black text-base md:text-2xl leading-none" data-id="element-229">
                <AnimatedNumber value={80} suffix="+" data-id="element-230" />
              </p>
              <p className="text-[9px] md:text-sm text-brand-200 font-medium leading-tight mt-0.5" data-id="element-231">
                recursos listos
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-wrap justify-center gap-1 md:gap-2 mb-6 md:mb-12 max-w-3xl mx-auto max-h-16 md:max-h-none overflow-hidden relative" data-id="element-232">
          {['Parciales', 'Orales', 'Planificación', 'Evaluación', 'Prácticas Docentes', 'Anatomía', 'Fisiología', 'Historia', 'Didáctica', 'Primeros Auxilios', 'Simulacros', 'Fichas', 'Plantillas', 'Rúbricas', 'Organización', 'Comunicación Docente'].map((tag, i) => <span key={i} className="px-1.5 py-0.5 md:px-3 md:py-1 bg-brand-800/80 text-brand-100 text-[9px] md:text-xs font-semibold rounded-full border border-brand-700" data-id="element-233">
              {tag}
            </span>)}
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-brand-900 to-transparent md:hidden" data-id="element-234"></div>
        </motion.div>

        {/* Color legend — desktop only (compact) */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="hidden md:block bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/20" data-id="element-235">
          <p className="font-bold mb-4 text-sm uppercase tracking-wider" data-id="element-236">
            Años y enfoques incluidos:
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-237">
            {[['bg-green-400', 'Primer año'], ['bg-yellow-400', 'Segundo y tercer'], ['bg-red-400', 'Prácticas'], ['bg-blue-400', 'Organización'], ['bg-purple-400', 'Evaluación'], ['bg-orange-400', 'Teóricas']].map(([color, label]) => <div key={label} className="flex items-center gap-1.5" data-id="element-238">
                <div className={`w-3 h-3 rounded-full ${color}`} data-id="element-239"></div>
                <span className="text-sm font-medium" data-id="element-240">{label}</span>
              </div>)}
          </div>
        </motion.div>

        {/* Mobile compact: tagline only */}
        <p className="md:hidden text-xs font-medium text-brand-200" data-id="element-241">
          Funciona desde hoy en cualquier dispositivo
        </p>
      </div>
    </section>;
}